<template>
  <section>
    <Paragraph class="font-jost-regular text-blue-dark text-[25px] leading-[30px] mt-10" text="Verification" />
    <div class="t-results text-lg mt-5">
      <section class="t-results-body grid grid-cols-2 gap-[90px] bg-white">
        <div>
          <div class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Block Number
            </div>
            <router-link
              v-if="transactionData.block !== null"
              class="col block px-2.5 flex relative text-blue-light cursor-pointer text-[16px]"
              :to="{ name: 'block', params: { chainNumber: transactionData.chainNumber || $route.params.chainNumber || $route.params.chain, blockNumber: transactionData.block } }"
            >
              #{{ transactionData.block }}
            </router-link>
            <div v-else class="col font-jost-regular px-2.5 text-blue-dark text-[16px]">N/A</div>
          </div>

          <div class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Chain Number
            </div>
            <router-link
              v-if="transactionData.chainNumber !== undefined && transactionData.chainNumber !== null"
              class="col text-blue-light cursor-pointer text-[16px]"
              :to="{ name: 'chain', params: { chainNumber: transactionData.chainNumber } }"
            >
              #{{ transactionData.chainNumber }}
            </router-link>
            <div v-else class="col text-[16px]">
              N/A
            </div>
          </div>

          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              {{ isNftTransfer ? 'Transfer amount' : 'Coin amount' }}
            </div>
            <div class="col flex px-2.5 gap-[10px]">
              <p class="block relative text-ellipsis text-blue-dark text-[16px]">
                {{ getBalance(coinValue.value) }} <span class="uppercase">GEEQ</span>
              </p>
            </div>
          </div>

          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Transaction Fee
            </div>
            <div class="col font-jost-regular px-2.5 text-blue-dark text-[16px]">
              {{ getBalance(transactionData.fee) }} <span class="uppercase">GEEQ</span>
            </div>
          </div>

          <div class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              From
            </div>
            <div class="col flex px-2.5 gap-[10px]">
              <router-link v-if="transactionData.from" class="block text-ellipsis text-[16px]" :to="{ name: 'account', params: { chainNumber: transactionData.chainNumber || $route.params.chainNumber || $route.params.chain, address: transactionData.from } }">
                <span class="text-blue-light text-[16px]">
                  {{ transactionData.from }}
                </span>
              </router-link>
              <span v-else class="text-[16px]">N/A</span>
              <CopyIcon
                v-if="transactionData.from"
                :isActive="checkIfIsEqual(transactionData.from, 'sender')"
                @click="copy(transactionData.from, 'sender')"
                title="Copy hash"
                class="min-w-[16px] h-[19px] inline-block cursor-pointer align-top"
              />
            </div>
          </div>

          <div class="first__col__last__row row text-left items-center text-blue-dark px-2.5 py-3.5 result-row flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              To
            </div>
            <div class="col flex px-2.5 gap-[10px]">
              <router-link v-if="transactionData.to" class="block text-ellipsis text-[16px]" :to="{ name: 'account', params: { chainNumber: transactionData.chainNumber || $route.params.chainNumber || $route.params.chain, address: transactionData.to } }">
                <span class="text-blue-light text-[16px]">
                  {{ transactionData.to }}
                </span>
              </router-link>
              <span v-else class="text-[16px]">N/A</span>
              <CopyIcon
                v-if="transactionData.to"
                :isActive="checkIfIsEqual(transactionData.to, 'receiver')"
                @click="copy(transactionData.to, 'receiver')"
                title="Copy hash"
                class="min-w-[16px] h-[19px] inline-block cursor-pointer align-top"
              />
            </div>
          </div>
        </div>

        <div>
          <div class="row text-left items-center text-blue-dark border-b border-[#2699FB] px-2.5 py-3.5 result-row flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Status
            </div>
            <div class="flex items-center gap-3 col font-jost-regular px-2.5 text-[16px]">
              <span
                class="px-2 py-1 text-base font-medium rounded uppercase"
                :class="transactionData.status === 'valid' ? 'bg-green-100 text-green-800' : transactionData.status === 'invalid' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'"
              >
                {{ transactionData.status || 'PENDING' }}
              </span>
            </div>
          </div>
          <div
            class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Signature
            </div>
            <div class="col flex items-center font-jost-regular px-2.5 text-[16px]">
              <p v-if="transactionData" class="block text-ellipsis text-[16px]">
                <span class="text-blue-dark">
                  {{ transactionData.signature }}
                </span>
              </p>
              <CopyIcon :isActive="checkIfIsEqual(transactionData.signature, 'signature')" @click="copy(transactionData.signature, 'signature')"
                title="Copy hash" class="min-w-[16px] h-[19px] inline-block cursor-pointer align-top" />
            </div>
          </div>

          <div class="row text-left items-center text-blue-dark px-2.5 py-3.5 result-row border-b border-[#2699FB] flex items-baseline">
            <div class="col font-jost-regular text-blue-dark block text-[16px] leading-[21px] flex relative">
              Timestamp
            </div>
            <div class="col font-jost-regular px-2.5 text-blue-dark text-[16px]">
              {{ getTxCreationDate() }}
            </div>
          </div>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import moment from 'moment';
import string from '@/helpers/string';

import CopyIcon from "@/components/svg/CopyIcon.vue";
import Paragraph from "@/components/web/ParagraphComponent";

export default {
  name: 'CreateProxyOverview',
  components: {
    CopyIcon,
    Paragraph
  },
  props: {
    transactionData: {
      type: Object,
      required: true
    },
    transactionRefHash: {
      type: String,
      required: true
    },
  },
  data() {
    return {
      copied: '',
      balanceDivisor: parseInt(process.env.VUE_APP_BALANCE_DIVISOR) || 1000000000,
    };
  },
  computed: {
    values() {
      const values = this.transactionData.values;
      if (Array.isArray(values)) {
        return values;
      }

      if (this.transactionData.value && typeof this.transactionData.value === 'object') {
        return [this.transactionData.value];
      }

      return [];
    },
    createdAt() {
      return this.transactionData?.createdAt || '';
    },
    coinValue() {
      const coinValue = this.values.find((item) => item && item.type === 'coin');
      return coinValue || { value: '', type: 'coin' };
    },
  },
  methods: {
    getBalance(amount) {
      if (amount === null || amount === undefined || amount === '') {
        return amount;
      }
      const parsedAmount = Number(amount);
      if (Number.isNaN(parsedAmount) || !this.balanceDivisor) {
        return amount;
      }
      return parsedAmount / this.balanceDivisor;
    },
    copy(hash, uId = 0) {
      string.copyToClipboard(hash);
      this.setCopied(hash + uId);
    },
    setCopied(hash) {
      this.copied = hash;
    },
    checkIfIsEqual(str, uId = 0) {
      return string.isTextEqualTo(str, this.copied, uId);
    },
    getTxCreationDate() {
      const createdAt = this.createdAt;
      if (!createdAt) {
        return '';
      }
      return moment(createdAt).format('YYYY-MM-DD HH:mm:ss');
    },
  }
}
</script>

<style scoped>
.col {
  flex: 1;
  width: calc(100% / 2);
}

.t-results-body {
  padding: 28px 21px;
  border-radius: 8px;
}

.t-results-body .row:first-child {
  border-top: unset;
}

@media (max-width: 992px) {
  .t-results-body {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .first__col__last__row {
    border-bottom: 1px solid #2699FB;
  }
}
</style>
